/**
 * Kid Notifications Query Hooks
 *
 * React Query hooks for child-facing notification operations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from './keys';
import { kidNotificationsService, type KidNotification } from '@/services/kidNotifications';

export function useKidNotifications(childId?: string) {
  return useQuery({
    queryKey: queryKeys.kidNotifications.list(childId!),
    queryFn: () => kidNotificationsService.getAll(childId!),
    enabled: !!childId,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });
}

export function useKidUnreadCount(childId?: string) {
  return useQuery({
    queryKey: queryKeys.kidNotifications.unreadCount(childId!),
    queryFn: () => kidNotificationsService.getUnreadCount(childId!),
    enabled: !!childId,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

export function useMarkKidNotificationRead(childId: string) {
  const queryClient = useQueryClient();
  const listKey = queryKeys.kidNotifications.list(childId);
  const countKey = queryKeys.kidNotifications.unreadCount(childId);

  return useMutation({
    mutationFn: (notificationId: string) => kidNotificationsService.markAsRead(childId, notificationId),
    onMutate: async (notificationId) => {
      await queryClient.cancelQueries({ queryKey: listKey });
      await queryClient.cancelQueries({ queryKey: countKey });
      const prevList = queryClient.getQueryData<KidNotification[]>(listKey);
      const prevCount = queryClient.getQueryData<number>(countKey);

      const wasUnread = prevList?.find((n) => n.id === notificationId)?.is_read === false;

      queryClient.setQueryData<KidNotification[]>(listKey, (old) =>
        (old ?? []).map((n) =>
          n.id === notificationId ? { ...n, is_read: true, read_at: new Date().toISOString() } : n
        )
      );

      if (wasUnread) {
        queryClient.setQueryData<number>(countKey, (old) => Math.max(0, (old ?? 0) - 1));
      }

      return { prevList, prevCount };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.prevList) queryClient.setQueryData(listKey, ctx.prevList);
      if (ctx?.prevCount !== undefined) queryClient.setQueryData(countKey, ctx.prevCount);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.kidNotifications.all });
    },
  });
}

export function useMarkAllKidNotificationsRead(childId: string) {
  const queryClient = useQueryClient();
  const listKey = queryKeys.kidNotifications.list(childId);
  const countKey = queryKeys.kidNotifications.unreadCount(childId);

  return useMutation({
    mutationFn: () => kidNotificationsService.markAllAsRead(childId),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: listKey });
      await queryClient.cancelQueries({ queryKey: countKey });
      const prevList = queryClient.getQueryData<KidNotification[]>(listKey);
      const prevCount = queryClient.getQueryData<number>(countKey);

      const now = new Date().toISOString();
      queryClient.setQueryData<KidNotification[]>(listKey, (old) =>
        (old ?? []).map((n) => (n.is_read ? n : { ...n, is_read: true, read_at: now }))
      );
      queryClient.setQueryData<number>(countKey, 0);

      return { prevList, prevCount };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prevList) queryClient.setQueryData(listKey, ctx.prevList);
      if (ctx?.prevCount !== undefined) queryClient.setQueryData(countKey, ctx.prevCount);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.kidNotifications.all });
    },
  });
}

export function useDeleteKidNotification(childId: string) {
  const queryClient = useQueryClient();
  const listKey = queryKeys.kidNotifications.list(childId);
  const countKey = queryKeys.kidNotifications.unreadCount(childId);

  return useMutation({
    mutationFn: (notificationId: string) => kidNotificationsService.delete(childId, notificationId),
    onMutate: async (notificationId) => {
      await queryClient.cancelQueries({ queryKey: listKey });
      await queryClient.cancelQueries({ queryKey: countKey });
      const prevList = queryClient.getQueryData<KidNotification[]>(listKey);
      const prevCount = queryClient.getQueryData<number>(countKey);

      const removed = prevList?.find((n) => n.id === notificationId);

      queryClient.setQueryData<KidNotification[]>(listKey, (old) =>
        (old ?? []).filter((n) => n.id !== notificationId)
      );
      if (removed && !removed.is_read) {
        queryClient.setQueryData<number>(countKey, (old) => Math.max(0, (old ?? 0) - 1));
      }

      return { prevList, prevCount };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.prevList) queryClient.setQueryData(listKey, ctx.prevList);
      if (ctx?.prevCount !== undefined) queryClient.setQueryData(countKey, ctx.prevCount);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.kidNotifications.all });
    },
  });
}

export function useAddKidNotificationToCache(childId: string) {
  const queryClient = useQueryClient();

  return (notification: KidNotification) => {
    const listKey = queryKeys.kidNotifications.list(childId);
    const countKey = queryKeys.kidNotifications.unreadCount(childId);

    // Dedupe by id: only treat as new arrival if not already in the cache.
    // Prevents double-counting when the socket emits the same event twice
    // (e.g. on reconnect or duplicate join-child-room).
    const existing = queryClient.getQueryData<KidNotification[]>(listKey) ?? [];
    const isNew = !existing.some((n) => n.id === notification.id);

    queryClient.setQueryData<KidNotification[]>(listKey, (old) => {
      if (!old) return [notification];
      const filtered = old.filter((n) => n.id !== notification.id);
      return [notification, ...filtered];
    });

    if (isNew && !notification.is_read) {
      queryClient.setQueryData<number>(countKey, (old) => (old ?? 0) + 1);
    }
  };
}
